import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import { Heart, Bookmark, Share2, CalendarPlus, Volume2, VolumeX, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = { reel: "Reel", workout: "Workout", meal: "Meal Plan", mental: "Mind & Body" };

function Reel({ post, onLike, onSave }) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const el = containerRef.current;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          const v = videoRef.current;
          if (!v) return;
          if (e.isIntersecting) v.play().catch(() => {});
          else v.pause();
        });
      },
      { threshold: 0.6 }
    );
    if (el) obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const share = () => {
    navigator.clipboard?.writeText(window.location.href);
    toast.success("Link copied to clipboard");
  };

  return (
    <div ref={containerRef} className="snap-start shrink-0 h-[80vh] max-h-[720px] w-full flex justify-center py-2">
      <div className="media-dark relative h-full aspect-[9/16] rounded-3xl overflow-hidden border border-[#2A2A2A] bg-black">
        <video
          ref={videoRef}
          src={post.media_url}
          poster={post.thumbnail}
          loop
          muted={muted}
          playsInline
          className="w-full h-full object-cover"
          onClick={() => { const v = videoRef.current; v.paused ? v.play() : v.pause(); }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30 pointer-events-none" />

        <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-black/50 backdrop-blur text-xs uppercase tracking-wider text-[#FF6B6B] border border-[#FF6B6B]/30">
          {TYPE_LABEL[post.type] || "Reel"}
        </span>

        <button
          data-testid={`reel-mute-${post.post_id}`}
          onClick={() => setMuted((m) => !m)}
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/50 backdrop-blur flex items-center justify-center text-white"
        >
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>

        {/* Right actions */}
        <div className="absolute right-3 bottom-28 flex flex-col items-center gap-5">
          <button data-testid={`reel-like-${post.post_id}`} onClick={() => onLike(post)} className="flex flex-col items-center gap-1">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center backdrop-blur transition-colors ${post.liked ? "bg-[#FF6B6B]" : "bg-black/50"}`}>
              <Heart className={`w-6 h-6 ${post.liked ? "text-black fill-black" : "text-white"}`} />
            </div>
            <span className="text-xs text-white font-semibold">{post.like_count}</span>
          </button>
          <button data-testid={`reel-save-${post.post_id}`} onClick={() => onSave(post)} className="flex flex-col items-center gap-1">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center backdrop-blur transition-colors ${post.saved ? "bg-[#4ECCD3]" : "bg-black/50"}`}>
              <Bookmark className={`w-6 h-6 ${post.saved ? "text-black fill-black" : "text-white"}`} />
            </div>
            <span className="text-xs text-white font-semibold">Save</span>
          </button>
          <button data-testid={`reel-share-${post.post_id}`} onClick={share} className="flex flex-col items-center gap-1">
            <div className="w-12 h-12 rounded-full bg-black/50 backdrop-blur flex items-center justify-center">
              <Share2 className="w-6 h-6 text-white" />
            </div>
            <span className="text-xs text-white font-semibold">Share</span>
          </button>
          <button data-testid={`reel-book-${post.post_id}`} onClick={() => navigate(`/discover?book=${post.author_id}`)} className="flex flex-col items-center gap-1">
            <div className="w-12 h-12 rounded-full bg-[#FF6B6B] flex items-center justify-center animate-pulse-glow">
              <CalendarPlus className="w-6 h-6 text-black" />
            </div>
            <span className="text-xs text-white font-semibold">Book</span>
          </button>
        </div>

        {/* Bottom author + caption */}
        <div className="absolute bottom-4 left-4 right-20">
          <button onClick={() => navigate(`/discover?book=${post.author_id}`)} className="flex items-center gap-2 mb-2">
            <img src={post.author?.picture} alt="" className="w-9 h-9 rounded-full object-cover border-2 border-[#FF6B6B]" />
            <span className="font-semibold text-white text-sm">{post.author?.name}</span>
          </button>
          <p className="text-sm text-white/90 leading-snug line-clamp-3">{post.caption}</p>
        </div>
      </div>
    </div>
  );
}

export default function Feed() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/posts");
      setPosts(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onLike = async (post) => {
    setPosts((ps) => ps.map((p) => p.post_id === post.post_id ? { ...p, liked: !p.liked, like_count: p.like_count + (p.liked ? -1 : 1) } : p));
    try { await api.post(`/posts/${post.post_id}/like`); } catch { load(); }
  };
  const onSave = async (post) => {
    setPosts((ps) => ps.map((p) => p.post_id === post.post_id ? { ...p, saved: !p.saved } : p));
    try {
      await api.post(`/posts/${post.post_id}/save`);
      toast.success(post.saved ? "Removed from saved" : "Saved to your library");
    } catch { load(); }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#FF6B6B] animate-spin" /></div>;
  }

  return (
    <div>
      <div className="mb-4">
        <h1 className="font-display font-bold text-3xl uppercase tracking-tight text-white">Your Feed</h1>
        <p className="text-sm text-[#A0A0A0]">Fresh reels, recipes & mindfulness from your coaches.</p>
      </div>
      <div className="h-[calc(100vh-180px)] overflow-y-auto snap-y snap-mandatory no-scrollbar" data-testid="feed-scroll-container">
        {posts.map((p) => (
          <Reel key={p.post_id} post={p} onLike={onLike} onSave={onSave} />
        ))}
      </div>
    </div>
  );
}
